import { diffDocuments } from '@westhetics/legal'
import { LegalDocument } from '@westhetics/legal/react'
import {
  DiffView,
  EmptyState,
  Field,
  FieldGrid,
  FilterSegments,
  ListRow,
  ListRows,
  Select,
  Skeleton,
  useBreakpoint,
} from '@westhetics/ui'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { useTemplateVersion, useTemplateVersions, type Template } from './api'
import { diffHeadline, summaryKindLabel, summaryStatusLabel, toDiffRows } from './diff'

/**
 * Aba Comparar (RN-DOC-45): quaisquer duas versões, ou o rascunho atual. Três leituras da mesma
 * diferença: o que mudou por cláusula, o texto com as palavras marcadas e os dois documentos como
 * a pessoa vai ler. O cálculo é do `@westhetics/legal`; nada é gravado.
 */

type Side = number | 'draft'
type View = 'text' | 'rendered'

function parseSide(raw: string | null): Side | null {
  if (raw === 'draft') return 'draft'
  const number = Number(raw)
  return Number.isInteger(number) && number > 0 ? number : null
}

export function TemplateCompareTab({ template }: { template: Template }) {
  const versions = useTemplateVersions(template.id)
  const [params, setParams] = useSearchParams()
  const [view, setView] = useState<View>('text')
  const isDesk = useBreakpoint('desk')

  const list = useMemo(() => versions.data ?? [], [versions.data])
  const hasDraft = Boolean(template.draft) && template.hasUnpublishedChanges
  const newest = list[0]?.number ?? null
  const secondNewest = list[1]?.number ?? null

  // Padrão: o que está por publicar contra a versão vigente; sem rascunho, as duas últimas versões.
  const to: Side | null = parseSide(params.get('to')) ?? (hasDraft ? 'draft' : newest)
  const from: Side | null = parseSide(params.get('from')) ?? (hasDraft ? newest : secondNewest)

  const fromVersion = useTemplateVersion(template.id, typeof from === 'number' ? from : null)
  const toVersion = useTemplateVersion(template.id, typeof to === 'number' ? to : null)
  const draftMarkdown = template.draft?.markdown ?? null
  const before = from === 'draft' ? draftMarkdown : (fromVersion.data?.markdown ?? null)
  const after = to === 'draft' ? draftMarkdown : (toVersion.data?.markdown ?? null)

  const diff = useMemo(
    () => (before !== null && after !== null ? diffDocuments(before, after) : null),
    [before, after],
  )
  const rows = useMemo(() => (diff ? toDiffRows(diff) : []), [diff])

  if (versions.isError) {
    return (
      <LoadError
        title="Não conseguimos carregar as versões"
        error={versions.error}
        onRetry={() => void versions.refetch()}
      />
    )
  }
  if (versions.isPending) {
    return (
      <div role="status" aria-label="Carregando">
        <Skeleton className="h-[320px] w-full" />
      </div>
    )
  }

  const optionCount = list.length + (hasDraft ? 1 : 0)
  if (optionCount < 2 || from === null || to === null) {
    return (
      <EmptyState
        className="max-w-[1120px]"
        title="Ainda não há o que comparar"
        description="A comparação mostra o que mudou entre duas versões, ou entre a versão vigente e o rascunho. Ela aparece quando o documento tem uma versão publicada e uma alteração depois dela."
      />
    )
  }

  const labelOf = (side: Side) => {
    if (side === 'draft') return 'Rascunho atual'
    const version = list.find((item) => item.number === side)
    return version ? `v${version.number} · ${version.label}` : `v${side}`
  }
  const setSide = (key: 'from' | 'to', value: string) => {
    const next = new URLSearchParams(params)
    next.set(key, value)
    // O outro lado fica fixo no que a tela mostra, mesmo que tenha vindo do padrão.
    if (!next.has('from')) next.set('from', String(from))
    if (!next.has('to')) next.set('to', String(to))
    setParams(next, { replace: true })
  }
  const options = (
    <>
      {hasDraft ? <option value="draft">Rascunho atual</option> : null}
      {list.map((version) => (
        <option key={version.id} value={version.number}>
          v{version.number} · {version.label}
        </option>
      ))}
    </>
  )

  const loadError = fromVersion.error ?? toVersion.error
  const sameSide = from === to

  return (
    <div className="flex flex-col gap-5">
      <FieldGrid className="max-w-[640px]">
        <Field label="De">
          <Select value={String(from)} onChange={(event) => setSide('from', event.target.value)}>
            {options}
          </Select>
        </Field>
        <Field label="Para">
          <Select value={String(to)} onChange={(event) => setSide('to', event.target.value)}>
            {options}
          </Select>
        </Field>
      </FieldGrid>

      {loadError ? (
        <LoadError
          title="Não conseguimos carregar o texto de uma das versões"
          error={loadError}
          onRetry={() => {
            void fromVersion.refetch()
            void toVersion.refetch()
          }}
        />
      ) : sameSide ? (
        <EmptyState
          className="max-w-[1120px]"
          title="Os dois lados são a mesma versão"
          description="Escolha versões diferentes em De e Para para ver o que mudou entre elas."
        />
      ) : !diff ? (
        <div role="status" aria-label="Carregando">
          <Skeleton className="h-[320px] w-full" />
        </div>
      ) : diff.isIdentical ? (
        <EmptyState
          className="max-w-[1120px]"
          title="Nenhuma diferença"
          description={`${labelOf(from)} e ${labelOf(to)} têm exatamente o mesmo texto.`}
        />
      ) : (
        <>
          <section aria-labelledby="compare-summary" className="max-w-[1120px]">
            <h2 id="compare-summary" className="font-display text-lead text-ink font-normal">
              O que mudou
            </h2>
            <p className="font-body text-secondary mt-1 text-[12px] leading-[1.6] font-light">
              De {labelOf(from)} para {labelOf(to)} · {diffHeadline(diff)}
            </p>
            <ListRows className="mt-3">
              {diff.summary.length === 0 ? (
                <ListRow
                  title="Só o texto mudou"
                  meta="Nenhuma cláusula, bloco ou variável entrou ou saiu; veja as linhas abaixo."
                />
              ) : (
                diff.summary.map((entry, index) => (
                  <ListRow
                    key={`${entry.kind}-${entry.label}-${index}`}
                    eyebrow={summaryKindLabel(entry)}
                    title={entry.label}
                    trailing={
                      <span className="font-body text-eyebrow text-secondary font-light tracking-normal">
                        {summaryStatusLabel(entry)}
                      </span>
                    }
                  />
                ))
              )}
            </ListRows>
          </section>

          <FilterSegments
            label="Como ver a diferença"
            value={view}
            onChange={setView}
            options={[
              { value: 'text', label: 'Texto' },
              { value: 'rendered', label: 'Como fica' },
            ]}
          />

          {view === 'text' ? (
            <DiffView
              rows={rows}
              beforeLabel={labelOf(from)}
              afterLabel={labelOf(to)}
              layout={isDesk ? 'split' : 'unified'}
            />
          ) : (
            <div className="desk:grid-cols-2 grid grid-cols-1 gap-4">
              {(
                [
                  [labelOf(from), before],
                  [labelOf(to), after],
                ] as const
              ).map(([label, markdown], index) => (
                <div key={index} className="border-hairline bg-surface min-w-0 border">
                  <div className="border-hairline bg-pampas eyebrow border-b px-4 py-3">
                    {label}
                  </div>
                  <div className="px-5 py-5">
                    <LegalDocument markdown={markdown ?? ''} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
